import type { AnyAction } from "@reduxjs/toolkit";
import type { EntitlementRejectedModalType } from "@v2/types/modalTypes";
import type { AgentBillingPlan } from "schemas/agent-billing";
import type { ErrorDetails } from "schemas/errors";
import type { ErrorUserFacingDetails } from "schemas/generated/errors";

import { TRPC_ERROR_CODES_BY_KEY } from "@trpc/server/unstable-core-do-not-import";
import { breadcrumb } from "@v2/infra/nonfatal";
import { isLocalhost } from "replo-runtime/shared/env";
import { appendErrorId } from "replo-utils/lib/errors";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import {
  CUSTOM_ERROR_KEY_TO_PROPS,
  DEFAULT_ERROR_MESSAGE_CONFIG,
  errorUserFacingDetailsSchema,
} from "schemas/errors";

const BILLING_ERROR_TYPE_TO_MODAL = {
  "billing.addPublishedElements": "publishedElementsEntitlementRejectedModal",
  "billing.addShopifyThemeProject":
    "shopifyThemeProjectEntitlementRejectedModal",
  "billing.addReploSite": "reploSiteEntitlementRejectedModal",
} as const satisfies Record<string, EntitlementRejectedModalType>;

type BillingErrorType = keyof typeof BILLING_ERROR_TYPE_TO_MODAL;

const AGENT_BILLING_ERROR_TYPE_TO_MODAL = {
  "agentBilling.shopifyThemeBuilder.publishedElements.exceeded":
    "agentBillingAddPublishedElementsModal",
  "agentBilling.shopifyThemeBuilder.projects.exceeded":
    "agentBillingAddShopifyThemeBuilderProjectsModal",
  "agentBilling.sites.exceeded": "agentBillingAddSitesModal",
  "agentBilling.credits.exceeded": "agentBillingAddCreditsModal",
  "agentBilling.upgradeRequired": "agentBillingUpgradeModal",
} as const satisfies Record<string, EntitlementRejectedModalType>;

type AgentBillingErrorType = keyof typeof AGENT_BILLING_ERROR_TYPE_TO_MODAL;

const errorUserFacingDetailsToErrorDetails = (
  errorUserFacingDetails: ErrorUserFacingDetails,
):
  | {
      errorDetails: ErrorDetails;
      type: "fullPageModal" | "toast";
      errorKey?: string;
      endpointName?: string;
    }
  | {
      errorDetails: ErrorDetails;
      type: BillingErrorType | AgentBillingErrorType;
      errorKey: EntitlementRejectedModalType;
      plan?: AgentBillingPlan;
    }
  | {
      type: "shopifyStorePassword";
      errorKey: "passwordRequired" | "wrongPassword";
    }
  | { type: "none"; errorKey?: string; endpointName?: string }
  | null => {
  if (errorUserFacingDetails.type === "uncommon_none") {
    return null;
  }
  if (errorUserFacingDetails.type === "shopifyStorePassword") {
    return {
      type: "shopifyStorePassword",
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      errorKey: errorUserFacingDetails.key as
        | "passwordRequired"
        | "wrongPassword",
    };
  }
  if (
    errorUserFacingDetails.type === "billing.addPublishedElements" ||
    errorUserFacingDetails.type === "billing.addShopifyThemeProject" ||
    errorUserFacingDetails.type === "billing.addReploSite"
  ) {
    return {
      type: errorUserFacingDetails.type,
      errorDetails: {
        header: errorUserFacingDetails.message,
        message: errorUserFacingDetails.detail ?? [],
        isRecoverable: false,
        isFullPage: false,
        usage: errorUserFacingDetails.usage,
      },
      errorKey: BILLING_ERROR_TYPE_TO_MODAL[errorUserFacingDetails.type],
    };
  }
  if (
    errorUserFacingDetails.type ===
      "agentBilling.shopifyThemeBuilder.publishedElements.exceeded" ||
    errorUserFacingDetails.type ===
      "agentBilling.shopifyThemeBuilder.projects.exceeded" ||
    errorUserFacingDetails.type === "agentBilling.sites.exceeded" ||
    errorUserFacingDetails.type === "agentBilling.credits.exceeded" ||
    errorUserFacingDetails.type === "agentBilling.upgradeRequired"
  ) {
    return {
      type: errorUserFacingDetails.type,
      errorDetails: {
        header: errorUserFacingDetails.message,
        message: errorUserFacingDetails.detail ?? [],
        isRecoverable: false,
        isFullPage: false,
        usage: errorUserFacingDetails.usage,
      },
      errorKey: AGENT_BILLING_ERROR_TYPE_TO_MODAL[errorUserFacingDetails.type],
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      plan: errorUserFacingDetails.plan as AgentBillingPlan | undefined,
    };
  }
  const errorDetails: ErrorDetails = {
    header: errorUserFacingDetails.message,
    message: errorUserFacingDetails.detail ?? [],
    isRecoverable: errorUserFacingDetails.isRecoverable ?? false,
    isFullPage: errorUserFacingDetails.type === "fullPageModal",
    usage: errorUserFacingDetails.usage,
  };

  if (errorUserFacingDetails.callToAction) {
    errorDetails.callToAction = exhaustiveSwitch(
      errorUserFacingDetails.callToAction,
    )({
      installApp: (): ErrorDetails["callToAction"] => ({
        type: "installApp" as const,
      }),
      reload: () => ({
        type: "callback" as const,
        name: "Refresh",
        onClick: () => window.location.reload(),
      }),
      closeModal: () => ({
        type: "closeModal" as const,
        name: "Close",
      }),
      navigateToProjectEditor: () => ({
        type: "custom" as const,
        callToActionType: errorUserFacingDetails.callToAction,
      }),
      goToHomepage: () => ({
        type: "callback" as const,
        name: "Go To Home",
        onClick: () => window.location.replace("/home"),
      }),
      navigateToLink: () => ({
        type: "custom" as const,
        callToActionType: errorUserFacingDetails.callToAction,
      }),
    });
  }
  return {
    type:
      errorUserFacingDetails.type === "fullPageModal"
        ? "fullPageModal"
        : "toast",
    errorDetails,
    errorKey: errorUserFacingDetails.key,
  };
};

export const getErrorDetails = (
  config:
    | {
        type: "rsaaOrRtkQueryAction";
        action: AnyAction;
      }
    | {
        type: "trpcError";
        userFacingDetails?: ErrorUserFacingDetails;
        code?: number;
        path?: string;
        url?: string;
        errorId?: string;
        isSuperuser?: boolean;
        errorMessage?: string;
      },
):
  | {
      errorDetails: ErrorDetails;
      type: "fullPageModal" | "toast";
      errorKey?: string;
      endpointName?: string;
    }
  | {
      errorDetails: ErrorDetails;
      type: BillingErrorType | AgentBillingErrorType;
      errorKey: EntitlementRejectedModalType;
      plan?: AgentBillingPlan;
    }
  | {
      type: "shopifyStorePassword";
      errorKey: "passwordRequired" | "wrongPassword";
    }
  | { type: "none"; errorKey?: string; endpointName?: string }
  | null => {
  if (
    config.type === "rsaaOrRtkQueryAction" &&
    !Boolean(config.action.type?.endsWith("_ERROR")) &&
    !Boolean(config.action.type?.endsWith("/rejected"))
  ) {
    return null;
  }

  // Note (Evan, 2024-04-26): Don't throw a toast when AI requests are aborted
  // (this can be user-triggered)
  if (
    config.type === "rsaaOrRtkQueryAction" &&
    config.action.meta?.aborted === true &&
    (config.action.meta?.arg?.endpointName === "aiAltText" ||
      config.action.meta?.arg?.endpointName === "aiTextV2" ||
      config.action.meta?.arg?.endpointName === "aiMobileResponsive" ||
      config.action.meta?.arg?.endpointName === "aiTemplate")
  ) {
    return null;
  }

  breadcrumb("Network request returned failure/rejected", "error", {
    type:
      config.type === "rsaaOrRtkQueryAction"
        ? config.action.type
        : config.userFacingDetails?.key,
  });

  if (config.type === "trpcError") {
    const errorDisplay = (() => {
      if (config.url) {
        return `${new URL(config.url).pathname.replace("/api/v1/trpc/", "").replaceAll("/", ".")}`;
      }
      if (config.path) {
        return `Procedure ${config.path}`;
      }
      return "Procedure";
    })();

    let errorUserFacingDetails = config.userFacingDetails;
    if (
      !errorUserFacingDetails &&
      config.code === TRPC_ERROR_CODES_BY_KEY.TOO_MANY_REQUESTS
    ) {
      errorUserFacingDetails = {
        type: "toast",
        key: "unknown",
        message: "Too many requests",
        detail:
          "You've made too many requests in a short time period. Please try again later, or reach out to support@replo.app for help.",
      };
    }

    if (!errorUserFacingDetails) {
      errorUserFacingDetails = {
        type: isLocalhost() ? "fullPageModal" : "toast",
        key: "unknown",
        message: "Unknown error",
        detail: config.isSuperuser
          ? `${config.errorMessage ?? "An unknown error has occurred."} ${errorDisplay} ${config.errorId}`
          : appendErrorId(
              "An unknown error has occurred. If this error persists, please reach out to support@replo.app for help.",
              config.errorId,
            ),
      };
    }
    const details = errorUserFacingDetailsToErrorDetails(
      errorUserFacingDetails,
    );
    if (
      details &&
      (details.type === "toast" || details.type === "fullPageModal") &&
      !details.errorDetails.callToAction &&
      details.errorKey === "unknown" &&
      config.isSuperuser
    ) {
      details.errorDetails.callToAction = {
        type: "callback",
        name: "Copy Report",
        onClick: () => {
          void navigator.clipboard.writeText(
            `Error ID: ${config.errorId ?? ""}\\nPath: ${errorDisplay}\\nAdmin: https://admin.replo.app/dashboard/error-inspector?errorId=${config.errorId ?? ""}&redirect=true`,
          );
          // Note: We avoid importing toast here to prevent circular dependencies
          // The toast notification is handled in handleTRPCClientError in error-handling.tsx
        },
      };
    }
    return details;
  }

  // Note (Evan, 2024-01-19): If the error contains userFacingDetails, just use those
  const errorUserFacingDetails = errorUserFacingDetailsSchema.safeParse(
    config.action?.payload?.data ?? config.action?.payload?.response,
  );
  if (errorUserFacingDetails.success) {
    return errorUserFacingDetailsToErrorDetails(errorUserFacingDetails.data);
  }

  const errorKey =
    config.action?.payload?.response?.key ?? config.action?.payload?.data?.key;
  const messageFromServer =
    config.action?.payload?.response?.message ??
    config.action?.payload?.data?.message;

  // Note (Evan, 2024-01-19): Otherwise, look in the custom error key map
  let errorDetails: ErrorDetails | null = null;
  if (errorKey) {
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    errorDetails = CUSTOM_ERROR_KEY_TO_PROPS[
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      errorKey as keyof typeof CUSTOM_ERROR_KEY_TO_PROPS
    ]?.({
      type: errorKey,
      serverMessage: messageFromServer,
      endpoint: config.action.meta?.arg?.endpointName,
      // Note (Noah, 2024-05-30, USE-994): Because of the cast above, we have to
      // re-cast to the nullable type. I tried a few different configurations and
      // this seems to be the least hacky way to have typescript represent the
      // actual type
    }) as ErrorDetails | null;
  }

  if (!errorDetails) {
    errorDetails = DEFAULT_ERROR_MESSAGE_CONFIG;
  }

  if (errorDetails.isFullPage) {
    return {
      type: "fullPageModal",
      errorDetails,
      errorKey,
      endpointName: config.action.meta?.arg?.endpointName,
    };
  }

  // Note (Evan, 2024-01-19): Suppress the toast in a few specific scenarios:
  const suppressToast = Boolean(
    // billing plan errors are already displayed in the billing plan modal
    errorKey?.startsWith("billingPlan") ||
      // "path already in use" errors are already displayed in the page data modal
      errorKey === "pathAlreadyInUse" ||
      // RTKQ throws a "ConditionError" to bail out of queries that are already in progress,
      // this is internal to RTKQ so we don't want to alert the user
      // (see https://github.com/reduxjs/redux-toolkit/issues/2501)
      config.action.meta?.condition,
  );

  if (!suppressToast) {
    return {
      type: "toast",
      errorDetails,
      errorKey,
      endpointName: config.action.meta?.arg?.endpointName,
    };
  }
  return {
    type: "none",
    errorKey,
    endpointName: config.action.meta?.arg?.endpointName,
  };
};
